import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import { injectIntl } from 'react-intl';

import { isRTL } from '../../../helpers/formatLocale';
import { formatURL } from '../../../helpers/formatURL';
import { ListingImageNextArrow, ListingImagePrevArrow } from '../../../helpers/swiper';

import cs from 'swiper/swiper-bundle.css';
import s from './ListingSwiperPhotos.css';

const ListingSwiperPhotos = ({
    id,
    listPhotos = [],
    coverPhoto = 0,
    size = 'x_medium_',
    title,
    intl: { locale },
    personalizedURL,
    updatedLocale,
    isResultLoading,
    isMapShow
}) => {
    const [loaded, setLoaded] = useState(false);
    const [listingPhotos, setListingPhotos] = useState([]);
    const swiperRef = useRef(null);

    // Initialize Swiper packages only on client-side
    const swiperPackage = typeof window !== 'undefined' ? require('swiper/react') : null;
    const swiperModule = typeof window !== 'undefined' ? require('swiper/modules') : null;
    const { Swiper, SwiperSlide } = swiperPackage || {};
    const { Pagination } = swiperModule || {};

    // Effect for initial setup
    useEffect(() => {
        setListingPhotos(listPhotos);
        setTimeout(() => setLoaded(true), 1);
    }, [listPhotos]);

    // Effect for locale changes
    useEffect(() => {
        if (locale !== updatedLocale && !isMapShow) {
            setLoaded(false);
            setTimeout(() => setLoaded(true), 1);
        }
    }, [locale, updatedLocale, isMapShow]);

    const goNext = () => {
        if (swiperRef.current?.swiper) {
            swiperRef.current.swiper.slideNext();
        }
    };

    const goPrev = () => {
        if (swiperRef.current?.swiper) {
            swiperRef.current.swiper.slidePrev();
        }
    };

    const imagepath = '/images/upload/' + size;
    const pagination = listingPhotos?.length > 0 ? { dynamicBullets: true } : {};

    const params = {
        loop: listingPhotos && listingPhotos.length > 1,
        lazy: true,
        pagination,
    };

    const renderSwiper = () => (
        <Swiper
            modules={[Pagination]}
            {...params}
            ref={swiperRef}
            rtl={isRTL(locale)}
        >
            {listingPhotos.map((item, itemIndex) => {
                const listingURL = personalizedURL
                    ? `/rooms/${formatURL(title)}-${id}${personalizedURL}`
                    : `/rooms/${formatURL(title)}-${id}`;

                return (
                    <SwiperSlide key={itemIndex}>
                        <div className={cx(s.sliderItem)}>
                            <a href={listingURL} target="_blank" rel="noopener noreferrer">
                                <div className={s.parent}>
                                    <div
                                        className={cx(s.imageContent)}
                                        style={{ backgroundImage: `url(${imagepath}${item.name})` }}
                                    />
                                </div>
                            </a>
                        </div>
                    </SwiperSlide>
                );
            })}
        </Swiper>
    );

    const renderLoader = () => (
        <div className={cx('col-md-12 col-sm-12 col-xs-12', s.sliderItem)}>
            <div className={s.parent}>
                <div className={cx(s.imageContent, "swiper-lazy")}>
                    <div className="swiper-lazy-preloader" />
                </div>
                <div className="swiper-lazy-loaded swiper-lazy-preloader-white" />
            </div>
        </div>
    );

    const renderNavigation = () => (
        <div className={'searchArrow'}>
            <ListingImagePrevArrow
                className={cx('searchArrowPrev')}
                onClick={goPrev}
            />
            <ListingImageNextArrow
                className={cx('searchArrowNext')}
                onClick={goNext}
            />
        </div>
    );

    return (
        <div className={cx(s.listPhotoContainer, 'searchSwiper')}>
            {!isResultLoading && !updatedLocale && loaded && listingPhotos?.length > 0
                ? renderSwiper()
                : renderLoader()}
            {!isResultLoading && !updatedLocale && loaded && listingPhotos?.length > 1 && renderNavigation()}
        </div>
    );
};

ListingSwiperPhotos.propTypes = {
    id: PropTypes.number,
    listPhotos: PropTypes.array,
    coverPhoto: PropTypes.number,
    size: PropTypes.string,
    title: PropTypes.string,
    intl: PropTypes.shape({
        locale: PropTypes.string
    }),
    personalizedURL: PropTypes.string,
    updatedLocale: PropTypes.string,
    isResultLoading: PropTypes.bool,
    isMapShow: PropTypes.bool
};

export default injectIntl(withStyles(cs, s)(ListingSwiperPhotos));